import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { usePaginatedQuery, ErrorHandler, InfiniteScrollComponent } from '@playbookux/playbook-web-core';

import { HighlightReelZeroState } from '../../components/zero-state';
import Item from './element';
import DownloadHighlightReel from './download';
import Tabs from '../../constants/tabs';
import withLayout from '../../../core/components/with-layout';
import ShareHighlightReelDialog from './share-highlight-reel-dialog';
import ModalWithoutClips from './modal-without-clips/index';

import {
  CardGroup,
  Container,
  CreateButton,
  Header,
  Loader,
} from './styles';

import {
  DELETE_HIGHLIGHT_REEL,
  HIGHLIGHT_REELS,
  LIMIT,
  ORDER,
  updateHighlightReelDeleted,
  DUPLICATE_HIGHLIGHT_REEL,
} from './graphql';

const HighlightReel = (props) => {
  const {
    companyId,
    history,
  } = props;

  const [highlightReelId, setHighlightReelId] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [highlightReel, setHighlightReel] = useState({});
  const [hlDownloadId, setHlDownloadId] = useState('');
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const [withoutClipsModal, setWithoutClipsModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [withoutClipsModalId, setWithoutClipsModalId] = useState('');

  const {
    loading,
    data,
    error,
    fetchMore,
    hasMore,
    refetch,
  } = usePaginatedQuery(HIGHLIGHT_REELS, {
    variables: {
      companyId,
      limit: LIMIT,
      offset: 0,
      order: ORDER,
    },
    fetchPolicy: 'network-only',
  }, {
    dataField: 'highlightReels',
  });

  const [deleteHighlightReel] = useMutation(DELETE_HIGHLIGHT_REEL,
    {
      update: updateHighlightReelDeleted(companyId, highlightReelId),
      onQueryUpdated(observableQuery) { return observableQuery.refetch(); },
    });

  const [duplicateHighlightReel, { loading: duplicateLoading }] = useMutation(DUPLICATE_HIGHLIGHT_REEL);

  const { highlightReels = [] } = data;

  useEffect(() => {
    if (!_.isEmpty(highlightReelId)) {
      deleteHighlightReel({
        variables: { id: highlightReelId },
      });
    }
  }, [highlightReelId, deleteHighlightReel]);

  const handleClickCreate = () => {
    history.push('/highlight-reel/create');
  };

  const handleDelete = (newHighlightReelId) => {
    setHighlightReelId(newHighlightReelId);
  };

  const handleEdit = (id) => {
    window.open(`/highlight-reel/${id}/edit`, '_blank');
  };

  const configOpenModal = (type, id) => {
    setModalType(type);
    setWithoutClipsModalId(id);
    setWithoutClipsModal(true);
  };

  const handleShare = (hr) => {
    if (hr.totalClips === 0) {
      configOpenModal('share', hr.id);
    } else {
      setHighlightReel(hr);
      setShowShareDialog(true);
    }
  };

  const handleCloseShare = () => {
    setShowShareDialog(false);
  };

  const handleView = (id, totalClips) => {
    if (totalClips === 0) {
      configOpenModal('view', id);
    } else {
      window.open(`/highlight-reel/${id}`, '_blank');
    }
  };

  const handleDownload = (id, totalClips) => {
    if (totalClips === 0) {
      configOpenModal('download', id);
    } else {
      setHlDownloadId(id);
      setDownloadModalOpen(true);
    }
  };

  const handleDuplicate = async (id) => {
    await duplicateHighlightReel({
      variables: {
        id,
        companyId,
      },
    });

    refetch();
  };

  if (!companyId) {
    return <Redirect to="/create-workspace" />;
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving highlight reels."
      />
    );
  }
  return (
    <Container>
      {withoutClipsModal && (
        <ModalWithoutClips
          id={withoutClipsModalId}
          onClose={() => setWithoutClipsModal(false)}
          onEdit={handleEdit}
          type={modalType}
        />
      )}
      <Header>
        <CreateButton onClick={handleClickCreate}>
          Create Highlight Reel
        </CreateButton>
      </Header>

      {loading && _.isEmpty(highlightReels) && (
        <Loader data-loader="highlight-reel-loader" />
      )}

      {!loading && _.isEmpty(highlightReels) && (
        <HighlightReelZeroState />
      )}

      {duplicateLoading && <Loader data-loader="duplicate-highlight-loader" />}

      {!_.isEmpty(highlightReels) && (
        <InfiniteScrollComponent
          useWindow
          hasMore={hasMore}
          onLoadMore={fetchMore}
        >
          {_.map(highlightReels, (reel) => (
            <CardGroup
              key={`${companyId} + ${reel.id}`}
              itemsPerRow={4}
            >
              <Item
                highlightReel={reel}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onShare={handleShare}
                onView={handleView}
                onDownload={handleDownload}
              />
            </CardGroup>
          ))}
        </InfiniteScrollComponent>
      )}

      <ShareHighlightReelDialog
        show={showShareDialog}
        handleClose={handleCloseShare}
        highlightReel={highlightReel}
        companyId={companyId}
      />

      <DownloadHighlightReel
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        highlightReelId={hlDownloadId}
      />
    </Container>
  );
};

HighlightReel.propTypes = {
  companyId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,

  }).isRequired,
};

HighlightReel.defaultProps = {
  companyId: '',
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  connect(mapStateToProps, null),
  withLayout({ selectedTab: Tabs.HIGHLIGHT_REEL }),
);

export default enhance(HighlightReel);
